import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import Modal from '../../components/common/Modal.jsx';
import apiClient from '../../services/api/apiClient.js';
import { exportTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import { formatDate } from '../finanzas-cxp/shared.js';

/**
 * "Estado de Cuenta" de un Socio — gap encontrado en la auditoría 2026-09-28
 * (D-AUDIT-SOCIOS): V1 (`ListadoSocios.jsx#jalaMovs/PDFOC/excel`) tiene un botón por
 * fila (ícono de lista) que abre un modal con el detalle de movimientos del socio
 * (Fecha/Observaciones/Reparto/Retiro/Saldo corrido) + exportación PDF/Excel propia
 * de ese detalle -- `SociosPage.jsx` (ResourcePage genérico) no tenía ningún botón
 * ni modal equivalente.
 *
 * El backend YA expone el endpoint necesario, sin cambios:
 * `GET /reportes/estado-cuenta/socio/:socio` (`estado-cuenta.service.js#findOneSocio`),
 * que concatena PorRetirarSocios (reparto) + Retiros (retirado) ordenados por fecha
 * -- mismo shape que V1 devolvía desde `URL_ESTADO_CUENTA + 'Socio/' + id`. Ese
 * endpoint se gatea con el permiso `reportes:ver` (flag `menuReportes` de V1, no
 * `menuSocios` -- ver `reportes.routes.js`), así que el botón se oculta si el
 * usuario no tiene ese permiso además de `socios:ver`.
 *
 * El saldo corrido (V1: `saldo = saldo + c.reparto - c.retirado`, acumulado línea a
 * línea en el render) NO lo devuelve el backend -- se calcula aquí en el cliente,
 * igual que V1.
 */
export default function EstadoCuentaSocioModal({ socio, onClose }) {
  const [movs, setMovs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [exportando, setExportando] = useState(null);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    setError(null);
    apiClient.get(`/reportes/estado-cuenta/socio/${socio._id}`)
      .then(({ data }) => { if (!cancelado) setMovs(data.data || []); })
      .catch((e) => { if (!cancelado) setError(e.message); })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [socio._id]);

  // Saldo corrido -- mismo cálculo acumulado que V1 (`ListadoSocios.jsx`).
  let saldoCorrido = 0;
  const filas = movs.map((m) => {
    saldoCorrido += Number(m.reparto || 0) - Number(m.retirado || 0);
    return { ...m, saldo: saldoCorrido };
  });

  const totRepartido = filas.reduce((s, f) => s + Number(f.reparto || 0), 0);
  const totRetirado = filas.reduce((s, f) => s + Number(f.retirado || 0), 0);

  const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(n || 0));

  // Columnas de V1: PDF = Fecha, Observaciones, Reparto, Retirado, Saldo; Excel = las mismas SIN Saldo (y numéricas).
  const columnasExport = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'reparto', label: 'Reparto', render: (r) => money(r.reparto) },
    { key: 'retirado', label: 'Retirado', render: (r) => money(r.retirado) },
    { key: 'saldo', label: 'Saldo', render: (r) => money(r.saldo) },
  ];
  const columnasExcel = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'reparto', label: 'Reparto' },
    { key: 'retirado', label: 'Retirado' },
  ];

  async function exportarPDF() {
    setExportando('pdf');
    try {
      await exportTablePdf(`Retiros ${socio.name}`, columnasExport, filas, {
        fileName: 'Retiros',
        totalCols: { observaciones: 'Total', reparto: money(totRepartido), retirado: money(totRetirado), saldo: money(saldoCorrido) },
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: e.message });
    } finally {
      setExportando(null);
    }
  }

  async function exportarExcel() {
    setExportando('excel');
    try {
      exportTableExcel(`Retiros ${socio.name}`, columnasExcel, filas, { fileName: `Retiros ${socio.name}`, sheetName: 'Retiros' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: e.message });
    } finally {
      setExportando(null);
    }
  }

  return (
    <Modal
      open onClose={onClose} title={`Estado de Cuenta de ${socio.name}`} width="max-w-4xl"
      footer={<button type="button" onClick={onClose} className="btn-outline">Cerrar</button>}
    >
      <div className="flex items-center gap-2 mb-3">
        <button type="button" onClick={exportarPDF} disabled={!!exportando} className="btn-outline text-xs px-3 py-1.5">
          {exportando === 'pdf' ? 'Generando...' : 'PDF'}
        </button>
        <button type="button" onClick={exportarExcel} disabled={!!exportando} className="btn-outline text-xs px-3 py-1.5">
          {exportando === 'excel' ? 'Generando...' : 'Excel'}
        </button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>
      <div className="card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Observaciones</th>
              <th className="px-3 py-2 text-right">Reparto</th>
              <th className="px-3 py-2 text-right">Retiro</th>
              <th className="px-3 py-2 text-right">Saldo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cargando && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Cargando...</td></tr>
            )}
            {!cargando && filas.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Sin movimientos</td></tr>
            )}
            {!cargando && filas.map((f, i) => (
              <tr key={i}>
                <td className="px-3 py-2 whitespace-nowrap">{formatDate(f.fecha)}</td>
                <td className="px-3 py-2">{f.observaciones}</td>
                <td className="px-3 py-2 text-right">{money(f.reparto)}</td>
                <td className="px-3 py-2 text-right">{money(f.retirado)}</td>
                <td className="px-3 py-2 text-right">{money(f.saldo)}</td>
              </tr>
            ))}
          </tbody>
          {!cargando && filas.length > 0 && (
            <tfoot>
              <tr className="font-semibold bg-slate-50">
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2">TOTAL</td>
                <td className="px-3 py-2 text-right">{money(totRepartido)}</td>
                <td className="px-3 py-2 text-right">{money(totRetirado)}</td>
                <td className="px-3 py-2 text-right">{money(saldoCorrido)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Modal>
  );
}
